import { MethodType, type MethodTypeRepository, Pipeline, PipelineEnvironment } from '@telespot/domain';
import { ParseBaseRepository } from '../../parse-base.repository';
import { MethodTypeTopology, ParseMethodTypeMapper } from './parse-method-type.mapper';
import { ResourceTopology } from './parse-resource.mapper';
import { CaseTypeTopology } from './parse-case-type.mapper';
import { ProtocolPipelineTopology } from './parse-protocol-pipeline.mapper';
import { ParsePipelineMapper, PipelineTopology } from './parse-pipeline.mapper';
import { ObjectTopology, SoftDeletableTopology } from '../../parse.topology';

export class ParseMethodTypeRepository extends ParseBaseRepository implements MethodTypeRepository {
  private static readonly COUNT_METHOD_TYPES_PAGINATION_LIMIT = 25;
  // Far above any real per-method-type assignment count; Parse's default query
  // limit (100) would silently truncate the join diff.
  private static readonly ASSIGNED_PIPELINES_QUERY_LIMIT = 1000;

  private readonly methodTypeMapper = new ParseMethodTypeMapper(this.parse);
  private readonly pipelineMapper = new ParsePipelineMapper(this.parse);

  public async getById(id: string): Promise<MethodType> {
    const reference = await this.subclasses
      .getSubclass(MethodTypeTopology.TABLE)
      .createWithoutData(id)
      .fetchWithInclude(
        [MethodTypeTopology.ANALYSIS_TYPES, [MethodTypeTopology.RESOURCES, ResourceTopology.RESOURCES].join('.')],
        this.options
      );

    return this.methodTypeMapper.toDomain(reference);
  }

  public async findForCaseType(caseTypeId: string): Promise<MethodType[]> {
    try {
      const caseType = await this.subclasses
        .getSubclass(CaseTypeTopology.TABLE)
        .createWithoutData(caseTypeId)
        .fetchWithInclude(
          [
            [CaseTypeTopology.METHOD_TYPES, MethodTypeTopology.ANALYSIS_TYPES].join('.'),
            [CaseTypeTopology.METHOD_TYPES, MethodTypeTopology.RESOURCES, ResourceTopology.RESOURCES].join('.'),
          ],
          this.options
        );

      return caseType.get(CaseTypeTopology.METHOD_TYPES).map((mt) => this.methodTypeMapper.toDomain(mt));
    } catch (error) {
      if (error.message === 'Object not found.') return [];
      throw error;
    }
  }

  public async getPipelinesAssigned(methodTypeId: string, env?: PipelineEnvironment): Promise<Pipeline[]> {
    const methodType = this.subclasses.getSubclass(MethodTypeTopology.TABLE).createWithoutData(methodTypeId);

    const protocolPipelineQuery = new this.parse.Query(ProtocolPipelineTopology.TABLE).equalTo(
      ProtocolPipelineTopology.METHOD_TYPE,
      methodType
    );

    const pipelineQuery = new this.parse.Query(PipelineTopology.TABLE).matchesKeyInQuery(
      ObjectTopology.ID,
      `${ProtocolPipelineTopology.PIPELINE}.${ObjectTopology.ID}`,
      protocolPipelineQuery
    );

    if (env) {
      pipelineQuery.equalTo(PipelineTopology.ENV, env);
    }

    const results = await pipelineQuery.find(this.options);

    return results.map((p) => this.pipelineMapper.toDomain(p));
  }

  public async anyForAssetType(assetTypeId: string): Promise<boolean> {
    const parseAssetType = this.subclasses.getSubclass('AssetType').createWithoutData(assetTypeId);

    const result = await new this.parse.Query(MethodTypeTopology.TABLE)
      .equalTo(MethodTypeTopology.ASSET_TYPE, parseAssetType)
      .select() // Only fetch objectIds to minimize data transfer
      .limit(1)
      .first(this.options);

    return result !== undefined;
  }

  public async countByAssetType(assetTypeId: string): Promise<number> {
    const parseAssetType = this.subclasses.getSubclass('AssetType').createWithoutData(assetTypeId);

    let total = 0;
    let lastId: string | undefined;
    let continueQuerying = true;

    while (continueQuerying) {
      const query = new this.parse.Query(MethodTypeTopology.TABLE);
      query.equalTo(MethodTypeTopology.ASSET_TYPE, parseAssetType);
      query.ascending(ObjectTopology.ID);
      query.select(); // Only fetch objectIds to minimize data transfer
      query.limit(ParseMethodTypeRepository.COUNT_METHOD_TYPES_PAGINATION_LIMIT);

      if (lastId) {
        query.greaterThan(ObjectTopology.ID, lastId);
      }

      const results = await query.find(this.options);
      total += results.length;

      continueQuerying = results.length === ParseMethodTypeRepository.COUNT_METHOD_TYPES_PAGINATION_LIMIT;
      if (continueQuerying && results.length > 0) {
        lastId = results.at(-1).id;
      }
    }

    return total;
  }

  public async anyForResource(resourceId: string): Promise<boolean> {
    const parseResource = this.subclasses.getSubclass(ResourceTopology.TABLE).createWithoutData(resourceId);

    const result = await new this.parse.Query(MethodTypeTopology.TABLE)
      .containedIn(MethodTypeTopology.RESOURCES, [parseResource])
      .select() // Only fetch objectIds to minimize data transfer
      .limit(1)
      .first(this.options);

    return result !== undefined;
  }

  public async countByPipeline(pipelineId: string): Promise<number> {
    const parsePipeline = this.subclasses.getSubclass(PipelineTopology.TABLE).createWithoutData(pipelineId);

    return await new this.parse.Query(ProtocolPipelineTopology.TABLE)
      .equalTo(ProtocolPipelineTopology.PIPELINE, parsePipeline)
      .count(this.options);
  }

  public async findForPipeline(pipelineId: string): Promise<MethodType[]> {
    const pipeline = this.subclasses.getSubclass(PipelineTopology.TABLE).createWithoutData(pipelineId);

    const protocolPipelineQuery = new this.parse.Query(ProtocolPipelineTopology.TABLE).equalTo(
      ProtocolPipelineTopology.PIPELINE,
      pipeline
    );

    const results = await new this.parse.Query(MethodTypeTopology.TABLE)
      .matchesKeyInQuery(
        ObjectTopology.ID,
        `${ProtocolPipelineTopology.METHOD_TYPE}.${ObjectTopology.ID}`,
        protocolPipelineQuery
      )
      .find(this.options);

    return results.map((mt) => this.methodTypeMapper.toDomain(mt));
  }

  public async getAll(searchTerm?: string, limit?: number, lastItemName?: string): Promise<MethodType[]> {
    const query = this.buildMethodTypeQuery(searchTerm);

    query.ascending(MethodTypeTopology.NAME);

    if (limit) {
      query.limit(limit);
    }

    if (lastItemName) {
      query.greaterThan(MethodTypeTopology.NAME, lastItemName);
    }

    const parseMethodTypes = await query.find(this.options);
    return parseMethodTypes.map((pmt) => this.methodTypeMapper.toDomain(pmt));
  }

  public async count(searchTerm?: string): Promise<number> {
    let total = 0;
    let lastId: string | undefined;
    let continueQuerying = true;

    while (continueQuerying) {
      const query = this.buildMethodTypeQuery(searchTerm);
      query.ascending(ObjectTopology.ID);
      query.select(); // Only fetch objectIds to minimize data transfer
      query.limit(ParseMethodTypeRepository.COUNT_METHOD_TYPES_PAGINATION_LIMIT);

      if (lastId) {
        query.greaterThan(ObjectTopology.ID, lastId);
      }

      const results = await query.find(this.options);
      total += results.length;

      continueQuerying = results.length === ParseMethodTypeRepository.COUNT_METHOD_TYPES_PAGINATION_LIMIT;
      if (continueQuerying && results.length > 0) {
        lastId = results.at(-1).id;
      }
    }

    return total;
  }

  public async delete(id: string): Promise<void> {
    const parseMethodType = this.subclasses.getSubclass(MethodTypeTopology.TABLE).createWithoutData(id);
    await parseMethodType.save(
      {
        [SoftDeletableTopology.DELETED_AT]: new Date(),
      },
      this.options
    );
  }

  public async save(methodType: MethodType): Promise<string> {
    const parseMethodType = this.methodTypeMapper.fromDomain(methodType);
    const saved = await parseMethodType.save(null, this.options);
    return saved.id;
  }

  public async savePipelinesAssigned(methodTypeId: string, pipelineIds: string[]): Promise<void> {
    const methodType = this.subclasses.getSubclass(MethodTypeTopology.TABLE).createWithoutData(methodTypeId);

    const existing = await new this.parse.Query(ProtocolPipelineTopology.TABLE)
      .equalTo(ProtocolPipelineTopology.METHOD_TYPE, methodType)
      .limit(ParseMethodTypeRepository.ASSIGNED_PIPELINES_QUERY_LIMIT)
      .find(this.options);

    // SET semantics: joins of un-assigned pipelines are destroyed, target
    // pipelines without a join gain exactly one (existing rows are reused
    // untouched; the Set also collapses duplicated input ids).
    const target = new Set(pipelineIds);
    const toDestroy = existing.filter((join) => !target.has(join.get(ProtocolPipelineTopology.PIPELINE)?.id));

    const alreadyJoined = new Set(existing.map((join) => join.get(ProtocolPipelineTopology.PIPELINE)?.id));
    const toCreate = [...target]
      .filter((pipelineId) => !alreadyJoined.has(pipelineId))
      .map((pipelineId) => {
        const join = new this.parse.Object(ProtocolPipelineTopology.TABLE);
        join.set(ProtocolPipelineTopology.METHOD_TYPE, methodType);
        join.set(
          ProtocolPipelineTopology.PIPELINE,
          this.subclasses.getSubclass(PipelineTopology.TABLE).createWithoutData(pipelineId)
        );
        return join;
      });

    await this.parse.Object.destroyAll(toDestroy, this.options);
    await this.parse.Object.saveAll(toCreate, this.options);
  }

  private buildMethodTypeQuery(searchTerm?: string): Parse.Query {
    const query = new this.parse.Query(MethodTypeTopology.TABLE);

    if (searchTerm && searchTerm.trim() !== '') {
      query.matches(MethodTypeTopology.NAME, new RegExp(this.escapeRegExp(searchTerm), 'i'));
    }

    return query;
  }
}
